import { useState } from "react";
import { useNavigate } from "react-router";
import { DfyOrderPaymentDetails } from "@repo/api/dfy";
import { TeamPermissions } from "@repo/api/team-roles";
import { useCurrentTeamMembership } from "@/lib/hooks/team-hooks";
import { Button } from "@/lib/components/Button/Button";
import Toast from "@/lib/components/Toast";
import { paths } from "@/App";
import { useTeamQuery } from "@/lib/queries/team-queries";
import { useDfyOrderStore } from "@/lib/stores/dfy-order-store/dfy-order-store";
import { AnalyticsApi } from "@/lib/api/analytics-api";
import { formatDfyCreditsToPrice } from "../dfy-order-form-content";
import { DfyStepProps } from "../dfy-order-form-steps";
import DfyStepHeader from "../components/DfyStepHeader";
import DfyStepNavigation from "../components/DfyStepNavigation";
import ImageRightsDialog from "../components/ImageRightsDialog";
import OrderDetailsSummary from "../components/OrderDetailsSummary";
import CostBreakdown from "../components/CostBreakdown";
import DfyCouponInput from "../components/DfyCouponInput";
import PaymentMethodPicker from "../components/PaymentMethodPicker";
import { DFY_ORDER_SUBMITTED_PARAM } from "../../dfy-order-submitted-hooks";
import EditorNotes from "../components/EditorNotes";
import AfterOrderSteps from "../components/AfterOrderSteps";
import { usePaymentMethod } from "../dfy-order-form-hooks";

function getPayLabel({
  paymentDetails,
}: {
  paymentDetails: DfyOrderPaymentDetails;
}) {
  if (paymentDetails.stripeCredits !== 0) {
    return `Pay ${formatDfyCreditsToPrice(paymentDetails.stripeCredits)}`;
  }

  if (paymentDetails.creditsFromBalance !== 0) {
    return (
      <span className="flex items-center">
        Pay
        <img
          src="/CreditIconWhite.svg"
          alt=""
          className="mr-0.5 mb-0.5 ml-1.5 h-3.5 w-3.5"
        />
        {paymentDetails.creditsFromBalance.toLocaleString()}
      </span>
    );
  }

  return "Place order";
}

export default function ReviewStep({ onBack }: DfyStepProps) {
  const navigate = useNavigate();
  const teamExternalId = useDfyOrderStore((state) => state.teamExternalId);
  const orderExternalId = useDfyOrderStore((state) => state.orderExternalId);
  const details = useDfyOrderStore((state) => state.order!.details);
  const service = useDfyOrderStore((state) => state.service);

  const { data: team } = useTeamQuery(teamExternalId);
  const teamMembership = useCurrentTeamMembership();
  const creditBalance = team!.billingInfo.creditBalance;
  const isOrganisation = !team!.billingInfo.showCredits;
  const hasBillingEditPermission = teamMembership!.permissions.includes(
    TeamPermissions.BILLING_EDIT,
  );

  const [rightsOpen, setRightsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    paymentMethod,
    handleChangePaymentMethod,
    creditCoverage,
    paymentDetails,
    totalCredits,
    allowedPaymentMethods,
  } = usePaymentMethod({
    creditBalance,
    isOrganisation,
    hasBillingEditPermission,
  });

  const payLabel = getPayLabel({ paymentDetails });
  const canPlaceOrder = allowedPaymentMethods.includes(paymentMethod);

  const handlePlaceOrder = () => {
    setRightsOpen(true);
  };

  const confirmAndSubmit = async () => {
    setIsSubmitting(true);
    void AnalyticsApi.logDfyOrderFormStepChanged(
      {
        orderExternalId,
        fromStep: "review",
        toStep: null,
        direction: "FORWARD",
        details,
      },
      teamExternalId,
    );
    try {
      const result = await service.submit(paymentMethod);
      if (result.type === "CHECKOUT") {
        window.location.assign(result.checkoutUrl);
        return;
      }
      navigate(
        `${paths.dfyOrder(teamExternalId, orderExternalId)}?${DFY_ORDER_SUBMITTED_PARAM}=true`,
        { replace: true },
      );
    } catch (error: any) {
      if (error.response?.data?.errorCode === "INVALID_COUPON") {
        service.removeCoupon();
        Toast.error("Coupon is no longer valid. Please review the new total.");
      } else {
        Toast.error("Failed to place order. Please try again.");
      }
      setIsSubmitting(false);
      setRightsOpen(false);
    }
  };

  return (
    <>
      <DfyStepHeader
        title="Review"
        description="Review your order and complete payment."
        action={
          <Button
            size="sm"
            disabled={!canPlaceOrder}
            onClick={handlePlaceOrder}
          >
            {payLabel}
          </Button>
        }
      />
      <div className="flex flex-col gap-8 py-6">
        <div className="flex flex-col gap-8">
          <OrderDetailsSummary details={details} />
          <CostBreakdown
            lineItems={paymentDetails.lineItems}
            coupon={paymentDetails.coupon}
            creditsFromBalance={paymentDetails.creditsFromBalance}
            total={paymentDetails.stripeCredits}
            titleAccessory={<DfyCouponInput />}
          />
          {totalCredits > 0 && (
            <PaymentMethodPicker
              paymentMethod={paymentMethod}
              handleChangePaymentMethod={handleChangePaymentMethod}
              creditCoverage={creditCoverage}
              totalCredits={totalCredits}
              isOrganisation={isOrganisation}
              allowedPaymentMethods={allowedPaymentMethods}
              hasBillingEditPermission={hasBillingEditPermission}
            />
          )}
          <EditorNotes
            value={details.freeText}
            onChange={(freeText) =>
              service.updateDetails((draft) => {
                draft.freeText = freeText;
              })
            }
          />
          <AfterOrderSteps />
        </div>
        <DfyStepNavigation
          onBack={onBack}
          onNext={handlePlaceOrder}
          nextLabel={payLabel}
          nextDisabled={!canPlaceOrder}
          showCostSummary={false}
        />
      </div>

      <ImageRightsDialog
        open={rightsOpen}
        onOpenChange={setRightsOpen}
        isSubmitting={isSubmitting}
        onConfirm={confirmAndSubmit}
      />
    </>
  );
}
